import { Location, RouteObject, matchRoutes, useLocation, useNavigate } from "react-router-dom";
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { routes as routeMap } from "../router";
import { ReactElement } from "react";
import { AppProvider } from "./AppProvider";
import { HISTORY_STATE_DROP_BROWSING_STACKS } from "constant/routes";
import { getDebugInfo } from "utils/debugUtil";
import TransitionList from "./TransitionList";
import { TransitionStage } from "./TransitionList/constants";
import AnimationStateProvider from "./AnimationStateProvider";
import { AnimationType } from "./useAnimationState";
import AnimationWrapper from "./AnimationWrapper";
import { useUAVisualTransition } from "hooks/useUAVisualTransition";

const MAX_PRESERVE_STACK = 5
const PLACEHOLDER = <div />

const ANIMATION_DURATION = 350
const ANIMATION_DELAY = 0
const ANIMATION_EXTRA_TIMEOUT = 300

const SkipAnimation = ({
    animationType,
    stage,
    onTransitionEnd,
    onInitFinish,
    onLeavePrepareFinish,
}: {
    animationType: AnimationType
    stage: TransitionStage
    onTransitionEnd: () => void
    onInitFinish: () => void
    onLeavePrepareFinish: () => void
}) => {
    useLayoutEffect(() => {
        if (animationType === AnimationType.none) {
            if (stage === TransitionStage.enter || stage === TransitionStage.leave) {
                onTransitionEnd()
            }
            if (stage === TransitionStage.preInit) {
                onInitFinish()
            }
            if (stage === TransitionStage.preLeave) {
                onLeavePrepareFinish()
            }
        }
    }, [
        animationType,
        onInitFinish,
        onLeavePrepareFinish,
        onTransitionEnd,
        stage,
    ])
    return <></>
}

type NavigationType = 'init' | 'next' | 'prev' | 'repl'

export function AppLayout() {
    const currentLocation = useLocation()
    const currentLiveHistoryIndex: number | undefined = currentLocation.state?.index

    const routes = useMemo(() => matchRoutes(routeMap, currentLocation) ?? [], [currentLocation])
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
    const selfIndex = routes?.findIndex(i => (i.route.element as ReactElement).type === AppLayout) ?? 0

    const currentLiveRoute = routes?.[selfIndex + 1]!.route.element as ReactElement

    const maxHistoryIndex = useRef<number>()
    const lastRenderedIndex = useRef<number>()

    if (maxHistoryIndex.current == null) {
        if (currentLiveHistoryIndex != null) {
            maxHistoryIndex.current = currentLiveHistoryIndex
        }
    }

    interface StackFrame {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        state: { [k: string]: any },
        location: ReturnType<typeof useLocation>,
        routes: NonNullable<ReturnType<typeof matchRoutes<RouteObject>>>,
        key: string
        component: ReactElement
    }

    const [pageStack, setPageStack] = useState<StackFrame[]>()
    const renderedStack = useRef<StackFrame[]>()
    const navigate = useNavigate();

    const [navigationType, setNavigationType] = useState<NavigationType>('init')
    const consumeUAVisualTransition = useUAVisualTransition()
    const [skipAnimation, setSkipAnimation] = useState(false)

    const lastRanLocation = useRef<Location>()
    useEffect(() => {
        if (lastRanLocation.current === currentLocation) {
            return
        }
        lastRanLocation.current = currentLocation

        const liveHistoryIndex = window.history.state?.usr?.index

        // FIXME
        if (liveHistoryIndex != null && liveHistoryIndex !== currentLiveHistoryIndex) {
            console.error('REACT ROUTER CORRUPTED')
        }

        if (liveHistoryIndex == null) {
            if (maxHistoryIndex.current != null) {
                navigate({ ...currentLocation }, { replace: true, state: { ...currentLocation.state, index: maxHistoryIndex.current + 1 } })
                if (process.env.NODE_ENV === 'development' || getDebugInfo().includes('ROUTE_LOG')) {
                    console.log('[AppLayout] set maxHistoryIndex to ' + (maxHistoryIndex.current + 1), currentLocation.state)
                }
                maxHistoryIndex.current += 1
            } else {
                navigate({ ...currentLocation }, { replace: true, state: { ...currentLocation.state, index: 1 } })
                if (process.env.NODE_ENV === 'development' || getDebugInfo().includes('ROUTE_LOG')) {
                    console.log('[AppLayout] set maxHistoryIndex to 1')
                }
                maxHistoryIndex.current = 1
            }
            return
        }

        if (maxHistoryIndex.current != null && liveHistoryIndex > maxHistoryIndex.current) {
            maxHistoryIndex.current = liveHistoryIndex
        }

        const hasUATransition = consumeUAVisualTransition()
        setSkipAnimation(hasUATransition)

        if (lastRenderedIndex.current == null || liveHistoryIndex === lastRenderedIndex.current) {
            if (renderedStack.current == null) {
                if (process.env.NODE_ENV === 'development' || getDebugInfo().includes('ROUTE_LOG')) {
                    console.log(`[AppLayout] == init == ${liveHistoryIndex}`)
                }
                setNavigationType('init')
                const stack = [
                    {
                        state: currentLocation.state,
                        component: currentLiveRoute,
                        routes: routes.slice(selfIndex + 1),
                        location: currentLocation,
                        key: String(liveHistoryIndex)
                    }
                ]
                setPageStack(stack)
                renderedStack.current = stack
            } else {
                if (process.env.NODE_ENV === 'development' || getDebugInfo().includes('ROUTE_LOG')) {
                    console.log(`[AppLayout] == repl == ${liveHistoryIndex}`)
                }
                setNavigationType('repl')
                const patchedStack = renderedStack.current.slice(0, renderedStack.current.length - 1).concat([
                    {
                        state: currentLocation.state,
                        component: currentLiveRoute,
                        routes: routes.slice(selfIndex + 1),
                        location: currentLocation,
                        key: String(liveHistoryIndex)
                    }
                ])
                if (currentLocation.state[HISTORY_STATE_DROP_BROWSING_STACKS]) {
                    const sliced = [
                        patchedStack[patchedStack.length - 1]
                    ]
                    setPageStack(sliced)
                    renderedStack.current = sliced
                } else {
                    setPageStack(patchedStack)
                    renderedStack.current = patchedStack
                }
            }
        } else if (liveHistoryIndex > lastRenderedIndex.current) {
            if (process.env.NODE_ENV === 'development' || getDebugInfo().includes('ROUTE_LOG')) {
                console.log(`[AppLayout] == next == ${lastRenderedIndex.current} -> ${liveHistoryIndex}`)
            }
            setNavigationType('next')
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const patchedStack = renderedStack.current!.concat([
                {
                    state: currentLocation.state,
                    component: currentLiveRoute,
                    routes: routes.slice(selfIndex + 1),
                    location: currentLocation,
                    key: String(liveHistoryIndex)
                }
            ])
            if (currentLocation.state[HISTORY_STATE_DROP_BROWSING_STACKS]) {
                const sliced = [
                    patchedStack[patchedStack.length - 1]
                ]
                setPageStack(sliced)
                renderedStack.current = sliced
            } else {
                setPageStack(patchedStack)
                renderedStack.current = patchedStack
            }
        } else {
            if (process.env.NODE_ENV === 'development' || getDebugInfo().includes('ROUTE_LOG')) {
                console.log(`[AppLayout] == prev == ${lastRenderedIndex.current} -> ${liveHistoryIndex}`)
            }
            setNavigationType('prev')
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            let targetIndex = renderedStack.current!.findIndex(i => i.key === String(liveHistoryIndex))
            let targetNotFound = false

            let sliceIndex = -1

            if (targetIndex !== -1) {
                sliceIndex = targetIndex + 1
            } else  {
                targetNotFound = true
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                sliceIndex = renderedStack.current!.findIndex(i => i.key > String(liveHistoryIndex))
            }

            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const patchedStack = renderedStack.current!.slice(0, sliceIndex)

            if (targetNotFound) {
                patchedStack.push({
                    state: currentLocation.state,
                    component: currentLiveRoute,
                    routes: routes.slice(selfIndex + 1),
                    location: currentLocation,
                    key: String(liveHistoryIndex)
                })
            }

            if (patchedStack.length === 0) {
                const initialStack = [
                    {
                        state: currentLocation.state,
                        component: currentLiveRoute,
                        routes: routes.slice(selfIndex + 1),
                        location: currentLocation,
                        key: String(liveHistoryIndex)
                    }
                ]
                setPageStack(initialStack)
                renderedStack.current = initialStack
                lastRenderedIndex.current = liveHistoryIndex
                return
            }

            setPageStack(patchedStack)
            renderedStack.current = patchedStack
        }
        lastRenderedIndex.current = liveHistoryIndex
    }, [consumeUAVisualTransition, currentLiveHistoryIndex, currentLiveRoute, currentLocation, navigate, routes, selfIndex])

    // Compute effective animation type
    const animationType: AnimationType = useMemo(() => {
        if (navigationType === 'init') return AnimationType.none
        if (skipAnimation) return AnimationType.none
        if (navigationType === 'repl') return AnimationType.fade
        if (navigationType === 'next') return AnimationType.next
        if (navigationType === 'prev') return AnimationType.back
        return AnimationType.none
    }, [navigationType, skipAnimation])

    const transitionItems = useMemo(() => {
        return pageStack?.map((frame, index, arr) => {
            const isActive = index === arr.length - 1
            const content = arr.length - index >= MAX_PRESERVE_STACK ? PLACEHOLDER : frame.component
            const transitionKey = `${frame.key}:${frame.location.pathname}`

            return {
                key: transitionKey,
                timeout: ANIMATION_DURATION + ANIMATION_DELAY + ANIMATION_EXTRA_TIMEOUT,
                asyncInit: true,
                initTimeout: 2000,
                asyncLeave: true,
                leavePrepareTimeout: 2000,
                render(
                    stage: TransitionStage,
                    visible: boolean,
                    onTransitionEnd: () => void,
                    currentAnimationType: AnimationType | undefined,
                    onInitFinish: () => void,
                    onLeavePrepareFinish: () => void,
                ) {
                    return (
                        <Fragment key={transitionKey}>
                            <AnimationStateProvider
                                stage={stage}
                                visible={visible}
                                type={currentAnimationType!}
                                delay={ANIMATION_DELAY}
                                duration={ANIMATION_DURATION}
                                onTransitionFinished={onTransitionEnd}
                                onLeavePrepareFinished={onLeavePrepareFinish}
                                onInitFinished={onInitFinish}
                            >
                                <AnimationWrapper
                                    duration={ANIMATION_DURATION}
                                    delay={ANIMATION_DELAY}
                                >
                                    <AppProvider
                                        active={isActive}
                                        routes={frame.routes}
                                        location={frame.location}
                                    >
                                        {content}
                                    </AppProvider>
                                </AnimationWrapper>
                            </AnimationStateProvider>
                            <SkipAnimation
                                animationType={currentAnimationType!}
                                stage={stage}
                                onTransitionEnd={onTransitionEnd}
                                onInitFinish={onInitFinish}
                                onLeavePrepareFinish={onLeavePrepareFinish}
                            />
                        </Fragment>
                    )
                },
            }
        }) ?? []
    }, [pageStack])

    return <TransitionList
        synchronizedEnterExit
        data={animationType}
        items={transitionItems}
    />
}
